import { useState, useEffect } from "react";
import type { Article } from "@/types";
import { ArticleStatus } from "@/types/enums";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import { Loader2 } from "lucide-react";

interface ArticleFormData {
  title: string;
  slug: string;
  content: string;
  category: string;
  tags: string;
  article_status_id: ArticleStatus;
  author: string;
}

interface ArticleFormProps {
  initialData?: Article;
  onSubmit: (data: Partial<Article>) => Promise<void>;
  isLoading: boolean;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");
}

function toFormData(article?: Article): ArticleFormData {
  if (!article) {
    return {
      title: "",
      slug: "",
      content: "",
      category: "",
      tags: "",
      article_status_id: ArticleStatus.Draft,
      author: "",
    };
  }
  return {
    title: article.title,
    slug: article.slug,
    content: article.content,
    category: article.category || "",
    tags: article.tags || "",
    article_status_id: article.article_status_id ?? ArticleStatus.Draft,
    author: article.author || "",
  };
}

export function ArticleForm({ initialData, onSubmit, isLoading }: ArticleFormProps) {
  const [form, setForm] = useState<ArticleFormData>(() => toFormData(initialData));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [autoSlug, setAutoSlug] = useState(!initialData);

  useEffect(() => {
    if (initialData) {
      setForm(toFormData(initialData));
      setAutoSlug(false);
    }
  }, [initialData]);

  function updateField(field: keyof ArticleFormData, value: string) {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug from title for new articles
      if (field === "title" && autoSlug) {
        updated.slug = slugify(value);
      }
      if (field === "slug") {
        setAutoSlug(false);
      }
      return updated;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.title.trim()) newErrors.title = "Title is required";
    if (!form.slug.trim()) newErrors.slug = "Slug is required";
    if (!form.content.trim()) newErrors.content = "Content is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    await onSubmit({
      title: form.title.trim(),
      slug: form.slug.trim(),
      content: form.content.trim(),
      category: form.category.trim() || null,
      tags: form.tags.trim() || null,
      article_status_id: form.article_status_id,
      author: form.author.trim() || null,
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{initialData ? "Edit Article" : "New Article"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="Article title"
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => updateField("slug", e.target.value)}
                placeholder="article-slug"
              />
              {errors.slug && <p className="text-sm text-destructive">{errors.slug}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
                placeholder="Article category"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="status">Status</Label>
              {/* Radix Select is string-valued — convert at the widget boundary only. */}
              <Select
                value={String(form.article_status_id)}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, article_status_id: Number(value) as ArticleStatus }))
                }
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(ArticleStatus.Draft)}>Draft</SelectItem>
                  <SelectItem value={String(ArticleStatus.Published)}>Published</SelectItem>
                  <SelectItem value={String(ArticleStatus.Archived)}>Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="author">Author</Label>
              <Input
                id="author"
                value={form.author}
                onChange={(e) => updateField("author", e.target.value)}
                placeholder="Author name"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                value={form.tags}
                onChange={(e) => updateField("tags", e.target.value)}
                placeholder="Comma-separated tags"
              />
              <p className="text-xs text-muted-foreground">
                Separate multiple tags with commas
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="content">Content *</Label>
            <textarea
              id="content"
              className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={form.content}
              onChange={(e) => updateField("content", e.target.value)}
              placeholder="Article content"
            />
            {errors.content && <p className="text-sm text-destructive">{errors.content}</p>}
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {initialData ? "Update Article" : "Create Article"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
